import * as React from 'react';
import { Surface, SurfaceHeader, SurfaceActionBar, SurfaceBody } from '../../atoms/Surface';
import { IconButton } from "--components/buttons/iconButton";
import { Heading5 } from "--components/typography";
import IcoClose from '@floji-internal/mui-icons/Close';
import styled, { css } from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import { FocusLock } from '../../atoms/FocusLock';
import ReactDOM from 'react-dom';
import { Elevation } from '--components/theme';
import { useBreakpoint } from "--components/hooks/useBreakpoint";

// Todo:    Mobile styling, we have no designs at the moment and didn't want to waste time styling something that may not be used
//          Add customizable/disableable transitions like popover? Not sure if there is a use case here though at the moment.
//          Add slide transition for docked / mobile layout rather than fade

const STRINGS = {
    closeTooltip: "Close"
}

const MODALSIZE: ModalSurfaceSizes = {
    small: { w: 320 },
    medium: { w: 560 },
    large: { w: 800 },
    full: { w: "100%", h: "100%" }
}

type ModalSize = { w: string | number, h?: string | number };
type ModalSurfaceSizeName = "small" | "medium" | "large" | "full";
type ModalDockPosition = "top" | "bottom";
type ModalSurfaceSizes = { [key in ModalSurfaceSizeName]: ModalSize };
type ModalSafeArea = { top: number, bottom: number, left: number, right: number }

// Scrim that overlays the document and prevents background interaction
const Scrim = styled.div<{ safeArea?: ModalSafeArea }>`
    position: fixed;
    ${props => props.safeArea ?
        css`top: ${props.safeArea.top}; left: ${props.safeArea.left}; bottom: ${props.safeArea.bottom}; right: ${props.safeArea.right};` :
        css`top: 0; right: 0; bottom: 0; left: 0;`
    }

    background: ${props => props.theme.palette.ui.scrim};
    opacity: 0;
    display: none;
    z-index: ${props => props.theme.zIndex.modal};
    backdrop-filter: blur(2px);

    /* Animations */
	&.fade-appear,
    &.fade-enter {
        opacity: 0;
        display: initial;
    }
    &.fade-appear-active,
    &.fade-enter-active,
    &.fade-appear-done,
    &.fade-enter-done {
        opacity: 1;
        display: initial;
        transition: opacity ${props => props.theme.transitions.toggle};
    }
    &.fade-exit {
        opacity: 1;
        display: initial;
    }
    &.fade-exit-active {
        opacity: 0;
        display: initial;
        transition: opacity ${props => props.theme.transitions.toggle};
    }
    &.fade-exit-done {
        opacity: 0;
        display: none;
    }
`;

// Container used for centering the modal
type ModalContainerProps = {
    dock?: ModalDockPosition
    safeArea?: ModalSafeArea
}
const ModalContainer = styled(FocusLock) <ModalContainerProps>`
    position: fixed;
    ${props => props.safeArea ?
        css`top: ${props.safeArea.top}; left: ${props.safeArea.left}; bottom: ${props.safeArea.bottom}; right: ${props.safeArea.right};` :
        css`top: 0; right: 0; bottom: 0; left: 0;`
    }

    display: flex;
    justify-content: center;
    align-items: ${props => !props.dock ?
        "center" :
        props.dock == "bottom" ?
            "flex-end" :
            "flex-start"
    };

    pointer-events: none;
    z-index: ${props => props.theme.zIndex.modal};
`;

// Modal surface styles
type ModalProps = {
    width: string | number
    height?: string | number
    isFull?: boolean
    dock?: ModalDockPosition
    padding?: boolean
}
const Modal = styled(Surface) <ModalProps>`   
    position: absolute;

    // Set the base width
    width: ${props => typeof props.width == "string" ?
        props.width :
        `${props.width}px`
    };
    
    // Optionally set a height
    height: ${props => props.height ?
        typeof props.height == "string" ?
            props.height :
            `${props.height}px` :
        "initial"};
    
    // Disable padding and border radius on full screen modal
    ${props => (props.isFull || !props.padding) && css`
        padding: 0;    
    `}
    ${props => props.isFull && css`
        border-radius: 0;
    `}


    // Set the appropriate max dimensions based on size and docked position
    ${props => !props.isFull && css`
        max-width: ${props.dock ? "100%" : "90%"};
        max-height: ${props.dock ? "100%" : "90%"};
    `}

    // Remove borders when docked at top or bottom of screen
    ${props => props.dock && css`
        border-radius: ${props.dock == "bottom" ?
            `${props.theme.radius.large} ${props.theme.radius.large} 0 0` :
            `0 0 ${props.theme.radius.large} ${props.theme.radius.large}`
        };
    `}

    pointer-events: auto;
    overflow: hidden;

	&.fade-appear,
    &.fade-enter {
        opacity: 0;
        display: flex;
    }
    &.fade-appear-active,
    &.fade-enter-active,
    &.fade-appear-done,
    &.fade-enter-done {
        opacity: 1;
        display: flex;
        transition: opacity ${props => props.theme.transitions.short};
    }
    &.fade-exit {
        opacity: 1;
        display: flex;
    }
    &.fade-exit-active {
        opacity: 0;
        display: flex;
        transition: opacity ${props => props.theme.transitions.short};
    }
    &.fade-exit-done {
        opacity: 0;
        display: none;
    }
`;

// The actual modal dialog
type ModalSurfaceProps = {
    open: boolean
    onClose: () => void                           // Close requested
    onClosed?: () => void                         // Close completed
    disableScrim?: boolean
    elevation?: Elevation
    noPadding?: boolean                         // Remove padding from Dialog
    size?: ModalSurfaceSizeName | ModalSize;    // Width[& height] of modal to show. Defaults to medium
    safeArea?: ModalSafeArea                    // Top, Bottom, Left, Right safe area on screen
    stayMounted?: boolean                       // Keep the modal mounted when not rendered
    header?: string | React.ReactNode           // Automagically add a header and close button
    actions?: React.ReactNode                   // Automagically build an actions area
    dock?: ModalDockPosition | "none"           // Override default dock behavior
    className?: string
}
type ModalSurfaceExposedProps = Omit<ModalSurfaceProps, "open" | "onClose"> & { onClose?(): void }

const ModalSurface: React.FC<ModalSurfaceProps> = (props) => {
    const {
        open, onClose, onClosed, safeArea,
        disableScrim, elevation = 2, header,
        actions, stayMounted, size = "medium",
        children, noPadding, dock: dockOverride
    } = props;
    const [shouldRender, setShouldRender] = React.useState(false);
    const pendingClose = React.useRef(0);
    const [breakpoint] = useBreakpoint();
    const element = React.useMemo(() => {
        const el = document.createElement("div")
        document.body.appendChild(el);
        return el
    }, [])

    React.useEffect(() => () => { document.body.removeChild(element) }, [])

    React.useEffect(() => {
        if (open) {
            setShouldRender(true);
            window.addEventListener("keydown", onEscKey);
        }
        return () => {
            window.removeEventListener("keydown", onEscKey);
        }
    }, [open]);

    const dock = dockOverride ?
        dockOverride == "none" ?
            undefined :
            dockOverride :
        breakpoint == "mobile" ?
            "bottom" :
            undefined;

    const onEscKey = (e: KeyboardEvent) => {
        if (e.key !== "Escape") return;
        onClose();
    }

    const transitionEnd = (elem: HTMLElement, callback: () => void) => {
        elem.addEventListener('transitionend', function _endListener(e) {
            // Some event jiggery pokery
            if (e.target !== elem) return;
            elem.removeEventListener('transitionend', _endListener);
            callback();
            if (open) {
                pendingClose.current++;
            } else {
                pendingClose.current--;
                if (pendingClose.current <= 0) {
                    setShouldRender(false);
                    onClosed && onClosed();
                }
            }
        });

    };

    // Get width and height from size prop
    const modalSize = typeof size == "object" ? size : MODALSIZE[size];

    if (!stayMounted && !shouldRender) return null;

    // If there is no header or actions render the modal and children only else use named slots
    const useNamedSlots = !!header || !!actions;
    const modalContents = useNamedSlots ?
        <>
            {header && <SurfaceHeader>
                <Heading5>{header}</Heading5>
                <IconButton tabIndex={-1} size="small" color="greyInverted" onClick={onClose} title={STRINGS.closeTooltip} icon={<IcoClose />} />
            </SurfaceHeader>}
            <SurfaceBody>
                {children}
            </SurfaceBody>
            {actions && <SurfaceActionBar>
                {actions}
            </SurfaceActionBar>}
        </> :
        children;

    return ReactDOM.createPortal(<>
        <CSSTransition in={open && !disableScrim} appear={true} classNames="fade" addEndListener={transitionEnd} unmountOnExit>
            <Scrim onClick={onClose} safeArea={safeArea} />
        </CSSTransition>

        <ModalContainer dock={dock} safeArea={safeArea} autoFocus>
            <CSSTransition in={open} appear={true} classNames="fade" addEndListener={transitionEnd}>
                <Modal
                    className={props.className}
                    as="dialog"
                    aria-modal={true}
                    elevation={elevation}
                    width={modalSize.w}
                    height={modalSize.h || undefined}
                    isFull={size == "full"}
                    padding={!noPadding}
                    dock={dock}>
                    {modalContents}
                </Modal>
            </CSSTransition>
        </ModalContainer>
    </>, element);
}

// Make life a little easier and provide a hook
const useModalSurface = () => {
    const [open, setOpen] = React.useState(false);

    return React.useMemo(() => Object.assign((dialogprops: React.PropsWithChildren<ModalSurfaceExposedProps>) => <ModalSurface
        {...dialogprops}
        open={open}
        onClose={() => {
            dialogprops.onClose?.();
            setOpen(false)
        }
        }>
        {dialogprops.children}
    </ModalSurface>, {
        close: () => { setOpen(false) },
        open: () => { setOpen(true) },
    }), [open])
}


export default ModalSurface;
export { ModalSurface, ModalSurfaceProps, ModalSurfaceSizeName, ModalSize, useModalSurface };

